import { Col, Form, Modal, Row } from "react-bootstrap";
import { Button } from "../../../../components/Formularios/Buttons/Button";
import { mdiCheckCircle, mdiKeyboardReturn } from "@mdi/js";
import { useEffect, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Switch, TextField, CircularProgress } from "@mui/material";
import { Box } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "react-toastify";
import { dadosUF } from "../../../../components/dadosFixos/UF";
import { dadosPais } from "../../../../components/dadosFixos/Pais";

interface FilialInterface {
  id_filial?: number;
  fantasia: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  cmun?: string;
  cep?: string;
  uf?: string;
  cuf?: string;
  pais?: string;
  cpais?: string;
  fone?: string;
  celular?: string;
  email?: string;
  responsavel?: string;
  situacao: number;
  id_empresa?: number;
}

interface DetalheModalProps {
  showModal: boolean;
  acao?: string;
  onClose: () => void;
  onSubmit: (formData: FilialInterface) => void;
  dadosFilial?: FilialInterface;
}

const ModalDetalheFilial: React.FC<DetalheModalProps> = ({
  showModal,
  acao,
  onClose,
  onSubmit,
  dadosFilial
}) => {
  const { register, handleSubmit, watch, setValue, reset, control } = useForm<FilialInterface>();
  const [loading, setLoading] = useState<boolean>(false);
  const [ativo, setAtivo] = useState<boolean>(true);

  useEffect(() => {
    if (showModal && dadosFilial) {
      setValue("fantasia", dadosFilial.fantasia || "");
      setValue("logradouro", dadosFilial.logradouro || "");
      setValue("numero", dadosFilial.numero || "");
      setValue("complemento", dadosFilial.complemento || "");
      setValue("bairro", dadosFilial.bairro || "");
      setValue("cidade", dadosFilial.cidade || "");
      setValue("cmun", dadosFilial.cmun || "");
      setValue("cep", dadosFilial.cep || "");
      setValue("uf", dadosFilial.uf || "");
      setValue("cpais", dadosFilial.cpais || "1058"); // Brasil como padrão
      setValue("fone", dadosFilial.fone || "");
      setValue("celular", dadosFilial.celular || "");
      setValue("email", dadosFilial.email || "");
      setValue("responsavel", dadosFilial.responsavel || "");
      setAtivo(dadosFilial.situacao === 1);
    } else if (showModal) {
      reset();
      setAtivo(true);
      setValue("cpais", "1058"); // Brasil como padrão
    }
  }, [showModal, dadosFilial]);

  const buscarCep = async (cep: string) => {
    const cepLimpo = cep.replace(/\D/g, '');
    
    if (cepLimpo.length < 8) {
      toast.error("CEP inválido! O CEP deve conter 8 dígitos.");
      return;
    }
    
    setLoading(true);
    
    try {
      const { data } = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      
      if (data.erro) {
        toast.error("CEP inválido ou não localizado!");
        return;
      }
      
      setValue("logradouro", data.logradouro || "");
      setValue("complemento", data.complemento || "");
      setValue("bairro", data.bairro || "");
      setValue("cidade", data.localidade || "");
      setValue("uf", data.uf || "");
      setValue("cmun", data.ibge || "");
      
      const cepFormatado = cepLimpo.replace(/^(\d{5})(\d{3})$/, "$1-$2");
      setValue("cep", cepFormatado);
      
      toast.success("CEP localizado com sucesso!");
    } catch (erro: any) {
      console.error("Erro ao buscar CEP:", erro);
      const msg = erro.response?.data.message === undefined ? erro.message : erro.response.data.message;
      toast.error(`Erro ao buscar CEP. Motivo: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const formatarCep = (value: string) => {
    const cepLimpo = value.replace(/\D/g, '');
    const cepLimitado = cepLimpo.slice(0, 8);
    
    if (cepLimitado.length > 5) {
      return `${cepLimitado.slice(0, 5)}-${cepLimitado.slice(5)}`;
    }
    
    return cepLimitado;
  };

  const handleFormSubmit = (data: FilialInterface) => {
    const formData = {
      ...data,
      situacao: ativo ? 1 : 0,
      id_empresa: dadosFilial?.id_empresa
    };
    onSubmit(formData);
  };

  return (
    <Modal
      show={showModal}
      className="modal-full-width"
      size="xl"
      keyboard={false}
      centered
    >
      <Modal.Header className="bg-white">
        <Modal.Title>{acao === "Novo" ? "Incluir Filial" : "Editar Filial"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <Modal.Body className="bg-white">
          <Box sx={{ width: "100%" }}>
            <Row>
              <Col xs={12} md={6}>
                <TextField
                  sx={{ width: "100%", mb: 3 }}
                  label="Nome Fantasia"
                  size="small"
                  {...register("fantasia", { required: true })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  disabled={acao === "Visualizar"}
                />
              </Col>
              <Col xs={12} md={4}>
                <TextField
                  sx={{ width: "100%", mb: 3 }}
                  label="Responsável"
                  size="small"
                  {...register("responsavel")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={acao === "Visualizar"}
                />
              </Col>
              <Col xs={12} md={2}>
                <label htmlFor="" className="form-label me-2">
                  Ativo
                </label>
                <Switch
                  checked={ativo}
                  onChange={() => setAtivo(!ativo)}
                  disabled={acao === "Visualizar"}
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} sm={6} md={6}>
                <TextField
                  sx={{ width: "100%", mb: 3 }}
                  label="Telefone"
                  size="small"
                  {...register("fone")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={acao === "Visualizar"}
                />
              </Col>
              <Col xs={12} sm={6} md={6}>
                <TextField
                  sx={{ width: "100%", mb: 3 }}
                  label="Celular"
                  size="small"
                  {...register("celular")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={acao === "Visualizar"}
                />
              </Col>
              <Col xs={12} sm={6} md={6}>
                <TextField
                  sx={{ width: "100%", mb: 3 }}
                  label="Email"
                  size="small"
                  {...register("email", { required: true })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  disabled={acao === "Visualizar"}
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} sm={6} md={2}>
                <Controller
                  name="cep"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      sx={{ width: "100%", mb: 3 }}
                      label="CEP"
                      size="small"
                      value={field.value || ""}
                      onChange={(e) => {
                        const formattedValue = formatarCep(e.target.value);
                        field.onChange(formattedValue);
                      }}
                      onBlur={(e) => {
                        field.onBlur();
                        buscarCep(e.target.value);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      disabled={loading || acao === "Visualizar"}
                      InputProps={{
                        endAdornment: loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null,
                      }}
                    />
                  )}
                />
              </Col>
              <Col xs={12} sm={6} md={4}>
                <TextField
                  sx={{ width: "100%", mb: 3 }}
                  label="Logradouro"
                  size="small"
                  {...register("logradouro")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={acao === "Visualizar"}
                />
              </Col>
              <Col xs={12} sm={6} md={4}>
                <TextField
                  sx={{ width: "100%", mb: 3 }}
                  label="Complemento"
                  size="small"
                  {...register("complemento")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={acao === "Visualizar"}
                />
              </Col>
              <Col xs={12} sm={6} md={2}>
                <TextField
                  sx={{ width: "100%", mb: 3 }}
                  label="Número"
                  size="small"
                  {...register("numero")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={acao === "Visualizar"}
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} sm={6} md={6}>
                <TextField
                  sx={{ width: "100%", mb: 3 }}
                  label="Bairro"
                  size="small"
                  {...register("bairro")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={acao === "Visualizar"}
                />
              </Col>
              <Col xs={12} sm={6} md={6}>
                <TextField
                  sx={{ width: "100%", mb: 3 }}
                  label="Cidade"
                  size="small"
                  {...register("cidade")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={acao === "Visualizar"}
                />
              </Col>
              <Col xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="uf-label">Estado</InputLabel>
                  <Controller
                    name="uf"
                    control={control}
                    render={({ field }) => (
                      <Select
                        sx={{ width: "100%", mb: 3 }}
                        labelId="uf-label"
                        label="Estado"
                        size="small"
                        onChange={(event: SelectChangeEvent) => {
                          field.onChange(event.target.value);
                        }}
                        value={field.value ?? ''}
                        disabled={acao === "Visualizar"}
                      >
                        {dadosUF.map((item) => (
                          <MenuItem key={item.uf} value={item.uf}>
                            {item.nome}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Col>
              <Col xs={12} sm={6} md={2}>
                <TextField
                  sx={{ width: "100%", mb: 3 }}
                  label="Cod. IBGE"
                  size="small"
                  {...register("cmun")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={acao === "Visualizar"}
                />
              </Col>
              <Col xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="pais-label">País</InputLabel>
                  <Controller
                    name="cpais"
                    control={control}
                    render={({ field }) => (
                      <Select
                        sx={{ width: "100%", mb: 3 }}
                        labelId="pais-label"
                        label="País"
                        size="small"
                        onChange={(event: SelectChangeEvent) => {
                          field.onChange(event.target.value);
                        }}
                        value={field.value ?? '1058'}
                        disabled={acao === "Visualizar"}
                      >
                        {dadosPais.map((item) => (
                          <MenuItem key={item.codpais} value={item.codpais}>
                            {item.pais}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Col>
            </Row>
          </Box>
        </Modal.Body>
        <Modal.Footer className="bg-white">
          <div className="d-flex my-2 gap-1 justify-content-between">
            <Button
              type="button"
              className="btn btn-outline-secondary rounded-2 d-flex align-items-center gap-1"
              descricaoBotao="Voltar"
              icone={mdiKeyboardReturn}
              onclick={onClose}
            />
            {acao !== "Visualizar" && (
              <Button
                type="submit"
                className="btn btn-success rounded-2 d-flex align-items-center gap-1"
                descricaoBotao="Salvar"
                icone={mdiCheckCircle}
              />
            )}
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalDetalheFilial; 